import React, {useEffect} from 'react';
import {UserApp} from './AuthProviderActions';
import {Avatar, Button, Modal, Slider, Space, Upload, UploadProps} from 'antd';
import {
    UploadOutlined,
} from '@ant-design/icons';
import {RcFile} from 'antd/es/upload';
import {useAlertDispatcherState} from '../AlertProvider/AlertProvider';
import {addAlert} from '../AlertProvider/AlertProviderActions';
import Cropper from 'react-easy-crop';
import {dataURLtoBlob, getCroppedImg} from '../../Components/cropImage';
import {QueryResponse, useParameterizedQuery} from 'react-fetching-library';
import {resetAvatar, uploadAvatar} from '../../Actions/Users/UsersActions';
import {useAuthDispatch} from './AuthProvider';
import {UserAppFromAdminResponse} from '../../Actions/Users/UsersDomain';

interface ViewProps {
    user: UserApp,
    setUser: React.Dispatch<React.SetStateAction<UserApp>>,
    setKeys: React.Dispatch<React.SetStateAction<number[]>>,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setLoadingMsg: React.Dispatch<React.SetStateAction<string | undefined>>,
    manageRequest: (r: QueryResponse<UserAppFromAdminResponse>) => void
}

interface Area {
    x: number;
    y: number;
    width: number;
    height: number;
}

export const UserAvatarForm: React.FunctionComponent<ViewProps> = (props) => {
    const {user, setUser, setKeys, setIsLoading, setLoadingMsg, manageRequest} = props;

    const [imageSrc, setImageSrc] = React.useState<string | null>(null);
    const [crop, setCrop] = React.useState({x: 0, y: 0});
    const [zoom, setZoom] = React.useState(1);
    const [modalVisible, setModalVisible] = React.useState(false);
    const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area|null>(null);
    const [finalImage, setFinalImage] = React.useState<string | null>(null);

    const uploadAvatarAction = useParameterizedQuery(uploadAvatar);
    const resetAvatarAction = useParameterizedQuery(resetAvatar);

    const AVATAR = React.useMemo(() => {
        const imageBase = finalImage ? finalImage : user.imagen ? user.imagen : undefined;
        const userImage = imageBase ? imageBase : undefined;
        const userInitials = user.imagen ? undefined : '---';

        const userSpan = userInitials ? <span style={{fontSize: 50}}>{userInitials}</span> : undefined;

        return <Avatar src={userImage} className={`nav-avatar${ user.id > 0 ? '' : ' disabled' }`} size={144}>{userSpan}</Avatar>;
    }, [finalImage, user.imagen, user.id]);

    const alertDispatch = useAlertDispatcherState();
    const authDispatch = useAuthDispatch();

    const beforeUpload: UploadProps['beforeUpload'] = React.useCallback((file: RcFile) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            addAlert('error', 'Solo se permiten imágenes.', 'error', alertDispatch);
            return Upload.LIST_IGNORE;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result as string);
            setModalVisible(true);
        };
        reader.readAsDataURL(file);

        return false; // prevenir carga automática
    }, [alertDispatch]);

    const onCropComplete = React.useCallback((_croppedArea, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleCrop = async () => {
        if (!imageSrc || !croppedAreaPixels) return;
        const cropped = await getCroppedImg(imageSrc, croppedAreaPixels, 1024);
        setFinalImage(cropped);
        setModalVisible(false);
        // message.success('Imagen recortada correctamente');
    };

    const handleResetImage = React.useCallback(() => {
        if (user.id > 0 && !resetAvatarAction.loading) {
            resetAvatarAction.query(user.id).then(r => {
                manageRequest(r);
            });
        }
    }, [manageRequest, resetAvatarAction, user.id]);

    useEffect(() => {
        if (user.id > 0 && finalImage !== null && !uploadAvatarAction.loading) {
            const blob = dataURLtoBlob(finalImage);
            setFinalImage(null);

            uploadAvatarAction.query({userId: user.id, imageBlob: blob}).then(r => {
                manageRequest(r);
            });
        }
    }, [authDispatch, finalImage, manageRequest, setKeys, setUser, uploadAvatarAction, user.id]);

    useEffect(() => {
        const isLoading = uploadAvatarAction.loading || resetAvatarAction.loading;
        const meg = isLoading ? 'Actualizando Imagen de usuario...' : undefined;
        setLoadingMsg(meg);

        setIsLoading(isLoading);
    }, [resetAvatarAction.loading, setIsLoading, setLoadingMsg, uploadAvatarAction.loading]);

    return (
        <React.Fragment>
            <Modal
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleCrop}
                okText="Recortar"
                cancelText="Cancelar"
                width={500}
                centered
            >
                <div className="profile-crop-container" style={{position: 'relative', width: '100%', height: 400}}>
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                            showGrid={false}
                        />
                    )}
                </div>
                <Slider
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={setZoom}
                    style={{marginTop: 16}}
                />
            </Modal>
            <Space direction="vertical"
                   align='center'
                   style={{display: 'flex', padding: '20px 0'}}>
                {AVATAR}
                <Space direction="horizontal" style={{marginTop: '20px'}}>
                    <Upload beforeUpload={beforeUpload}
                            showUploadList={false}
                            accept="image/png,image/jpeg,image/webp"
                    >
                        <Button type="primary" icon={<UploadOutlined/>} disabled={user.id <= 0}>
                            Cambiar imagen
                        </Button>
                    </Upload>
                    <Button variant="outlined" onClick={handleResetImage} disabled={user.id <= 0}>
                        Resetear imagen
                    </Button>
                </Space>

            </Space>
        </React.Fragment>
    );
};
